import { z } from 'zod'
import {
  displayNameSchema,
  emailSchema,
  ianaTimeZoneSchema,
  localeSchema,
  organizationDisplayNameSchema,
  publicIdSchema,
  utcTimestampSchema,
} from './primitives'
import { passwordSchema } from './auth'
import { userStatusSchema, type User } from './user'

/**
 * Self-Service Account & Profile (Wave T). `GET /me/profile` (API-054).
 *
 * Deliberately excludes every authorization-bearing field (`globalRole`,
 * competition roles, judge assignments) — GOV-026 draws the line at "the
 * caller's own self-editable profile plus read-only account facts", and this
 * DTO is that line. No secret ever appears here (AC-052): no password hash,
 * no session/reset token.
 */
export const meProfileDtoSchema = z.object({
  id: publicIdSchema,
  email: emailSchema,
  displayName: displayNameSchema,
  /** GOV-028/BR-093: self-declared only, never a verified organization signal. */
  organizationDisplayName: organizationDisplayNameSchema.nullable(),
  accountStatus: userStatusSchema,
  emailVerificationStatus: z.enum(['verified', 'unverified']),
  locale: localeSchema,
  timezone: ianaTimeZoneSchema,
  /**
   * DM-033/API-061/PR-044: what Security Settings can offer this account.
   * `hasLocalPassword` covers both the `password` and `dev_password`
   * providers — either is a "local password exists" fact from the UI's
   * point of view.
   */
  authenticationMethods: z.object({
    hasLocalPassword: z.boolean(),
    hasOidc: z.boolean(),
  }),
  createdAt: utcTimestampSchema,
  /** API-057/AC-059: the email a pending change would switch to, or null if none is pending. Never the authoritative login email until verified. */
  pendingEmail: emailSchema.nullable(),
  pendingEmailExpiresAt: utcTimestampSchema.nullable(),
})
export type MeProfileDto = z.infer<typeof meProfileDtoSchema>

/**
 * `PATCH /me/profile` (API-055). `z.strictObject` is the enforcement
 * mechanism for GOV-026/AC-054: any key not listed here — `role`,
 * `globalRole`, `status`, `accountStatus`, competition role fields, whatever
 * an attacker tries — fails validation before a handler ever runs, rather
 * than relying on the handler to remember to ignore it. Every field is
 * optional; the handler updates only the keys actually present.
 */
export const updateMeProfileRequestSchema = z
  .strictObject({
    displayName: displayNameSchema.optional(),
    /** `null` clears the field — it is optional/self-declared, not required. */
    organizationDisplayName: organizationDisplayNameSchema.nullable().optional(),
    locale: localeSchema.optional(),
    timezone: ianaTimeZoneSchema.optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be provided.',
  })
export type UpdateMeProfileRequest = z.infer<typeof updateMeProfileRequestSchema>

export function toMeProfileDto(
  user: User,
  authenticationMethods: { hasLocalPassword: boolean; hasOidc: boolean },
  pendingEmailChange?: { newEmail: string; expiresAt: string } | null,
): MeProfileDto {
  return {
    id: user.id,
    email: user.email,
    displayName: user.displayName,
    organizationDisplayName: user.organizationDisplayName,
    accountStatus: user.status,
    emailVerificationStatus: user.emailVerifiedAt === null ? 'unverified' : 'verified',
    locale: user.locale,
    timezone: user.timezone,
    authenticationMethods,
    createdAt: user.createdAt,
    pendingEmail: pendingEmailChange?.newEmail ?? null,
    pendingEmailExpiresAt: pendingEmailChange?.expiresAt ?? null,
  }
}

// ---------------------------------------------------------------------------
// Change Password (API-056, SEC-024/025/026, BR-088/089).
// ---------------------------------------------------------------------------

export const changeMyPasswordRequestSchema = z.strictObject({
  currentPassword: z.string().min(1).max(200),
  newPassword: passwordSchema,
})
export type ChangeMyPasswordRequest = z.infer<typeof changeMyPasswordRequestSchema>

// ---------------------------------------------------------------------------
// Session list / revoke (API-059/060, DATA-038, SEC-029).
// ---------------------------------------------------------------------------

/**
 * Deliberately minimal (DATA-038/SEC-029): no IP, no raw User-Agent, no
 * session token or its hash — just enough for a person to recognize their
 * own devices and pick one to sign out.
 */
export const meSessionDtoSchema = z.object({
  id: publicIdSchema,
  createdAt: utcTimestampSchema,
  lastSeenAt: utcTimestampSchema,
  /** e.g. "chrome:windows"; null if the session predates this classification or the UA was unrecognized. */
  clientLabel: z.string().nullable(),
  isCurrent: z.boolean(),
})
export type MeSessionDto = z.infer<typeof meSessionDtoSchema>

// ---------------------------------------------------------------------------
// Self-Service Role & Permission View (Wave U Phase 2, PR-049/UX-042/BR-096,
// AC-067). `GET /me/competition-roles`.
// ---------------------------------------------------------------------------

/**
 * One competition the caller holds at least one role in, and every role held
 * there. BR-096: multiple roles in the same competition must never be
 * collapsed into one value — `roles` is the full set, not just the first or
 * "highest" one. This is a pure read of `competition_roles` (already the
 * authorization-authoritative table, see `CompetitionRolesRepository`) plus
 * the competition's own name for display — no derived or inferred field, and
 * no mutation capability anywhere in this DTO or the endpoint that returns
 * it.
 */
export const meCompetitionRoleDtoSchema = z.object({
  competitionId: publicIdSchema,
  competitionName: z.string(),
  roles: z.array(z.string()),
})
export type MeCompetitionRoleDto = z.infer<typeof meCompetitionRoleDtoSchema>

// ---------------------------------------------------------------------------
// Change Email (API-057/058, SEC-024/027, PR-043, BR-091, AC-059).
// ---------------------------------------------------------------------------

/**
 * `POST /me/email`. `currentPassword` is required only at the handler level,
 * and only when the account has a local password identity — an OIDC-only
 * account has no local secret to re-check, so the route skips that branch
 * rather than the schema making the field conditionally required.
 */
export const requestMyEmailChangeRequestSchema = z.strictObject({
  newEmail: emailSchema,
  currentPassword: z.string().min(1).max(200).optional(),
})
export type RequestMyEmailChangeRequest = z.infer<typeof requestMyEmailChangeRequestSchema>

/** `POST /me/email/verify`. Same id+token shape as the password-reset confirm request. */
export const verifyMyEmailChangeRequestSchema = z.strictObject({
  id: publicIdSchema,
  token: z.string().min(1).max(500),
})
export type VerifyMyEmailChangeRequest = z.infer<typeof verifyMyEmailChangeRequestSchema>

// ---------------------------------------------------------------------------
// Personal Data Export — Right to Access (PRIV-003, API-098, v3.98).
// `GET /me/data-export`. Read-only, self-service, no admin review needed
// (unlike Erasure below, which is destructive and requires review).
// Deliberately scoped to rows the caller directly owns (`user_id = caller`
// across the relevant tables) — third-party-derived joins (e.g. what a
// teammate can see about them) are out of scope for this delivery, the same
// phased-disclosure posture Member Eligibility used across its Phases.
// ---------------------------------------------------------------------------

export const myDataExportDtoSchema = z.object({
  generatedAt: utcTimestampSchema,
  profile: z.object({
    id: publicIdSchema,
    email: emailSchema,
    displayName: displayNameSchema,
    organizationDisplayName: organizationDisplayNameSchema.nullable(),
    avatarUrl: z.string().nullable(),
    locale: localeSchema,
    timezone: ianaTimeZoneSchema,
    globalRole: z.string(),
    createdAt: utcTimestampSchema,
    lastLoginAt: utcTimestampSchema.nullable(),
  }),
  /** Never includes `passwordHash`/token secrets — provider + timestamps only. */
  authenticationMethods: z.array(
    z.object({
      provider: z.string(),
      createdAt: utcTimestampSchema,
      lastUsedAt: utcTimestampSchema.nullable(),
    }),
  ),
  competitionRoles: z.array(
    z.object({
      competitionId: publicIdSchema,
      competitionName: z.string(),
      roles: z.array(z.string()),
    }),
  ),
  teamMemberships: z.array(
    z.object({
      competitionId: publicIdSchema,
      competitionName: z.string(),
      teamId: publicIdSchema,
      teamName: z.string(),
      role: z.string(),
      joinedAt: utcTimestampSchema,
    }),
  ),
  submissions: z.array(
    z.object({
      competitionId: publicIdSchema,
      competitionName: z.string(),
      teamId: publicIdSchema,
      teamName: z.string(),
      submissionId: publicIdSchema,
      status: z.string(),
      submittedAt: utcTimestampSchema.nullable(),
    }),
  ),
  eligibilityRecords: z.array(
    z.object({
      competitionId: publicIdSchema,
      competitionName: z.string(),
      status: z.string(),
      schoolName: z.string().nullable(),
      departmentName: z.string().nullable(),
      studentNumber: z.string().nullable(),
      updatedAt: utcTimestampSchema,
    }),
  ),
  judgeProfiles: z.array(
    z.object({
      competitionId: publicIdSchema,
      competitionName: z.string(),
      organization: z.string().nullable(),
      title: z.string().nullable(),
      bio: z.string().nullable(),
    }),
  ),
  judgeConflictsDeclared: z.array(
    z.object({
      competitionId: publicIdSchema,
      competitionName: z.string(),
      submissionId: publicIdSchema,
      reason: z.string().nullable(),
      source: z.string(),
      createdAt: utcTimestampSchema,
    }),
  ),
  /**
   * The caller's own most recent platform actions, newest first, capped —
   * a full unbounded audit history export is out of scope for this
   * delivery (see `truncatedAuditActions`).
   */
  recentAuditActions: z.array(
    z.object({
      competitionId: publicIdSchema.nullable(),
      action: z.string(),
      entityType: z.string(),
      createdAt: utcTimestampSchema,
    }),
  ),
  truncatedAuditActions: z.boolean(),
  erasureRequests: z.array(
    z.object({
      id: publicIdSchema,
      status: z.string(),
      requestedAt: utcTimestampSchema,
      reviewedAt: utcTimestampSchema.nullable(),
    }),
  ),
})
export type MyDataExportDto = z.infer<typeof myDataExportDtoSchema>

// ---------------------------------------------------------------------------
// Account Erasure — self-service REQUEST only (PRIV-003/004, API-099, v3.98).
// Approved product decision: request + Platform Admin review/execute, never
// fully automated instant self-service deletion (irreversible, touches
// competition historical integrity). See `platform.ts` for the admin-side
// review/execute schemas.
// ---------------------------------------------------------------------------

/**
 * `POST /me/erasure-requests`. Same fresh-authentication shape as
 * `requestMyEmailChangeRequestSchema` — `currentPassword` required only when
 * the account has a local password identity, checked at the handler level.
 */
export const requestMyErasureRequestSchema = z.strictObject({
  reason: z.string().trim().max(1000).optional(),
  currentPassword: z.string().min(1).max(200).optional(),
})
export type RequestMyErasureRequest = z.infer<typeof requestMyErasureRequestSchema>

export const myErasureRequestDtoSchema = z.object({
  id: publicIdSchema,
  status: z.enum(['pending', 'approved', 'rejected', 'cancelled']),
  requestedAt: utcTimestampSchema,
  reviewedAt: utcTimestampSchema.nullable(),
  reviewReason: z.string().nullable(),
})
export type MyErasureRequestDto = z.infer<typeof myErasureRequestDtoSchema>
